import type { Dispatch } from 'react'

import { buildViewOptionsPanelProps } from '~/client/src/shared/components/ViewOptions/buildViewOptionsPanelProps'
import type { IViewOptionsPanelProps } from '~/client/src/shared/components/ViewOptions/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import type { IColumnConfig } from '~/client/src/shared/hooks/useColumnConfig'
import type {
  ListAction,
  ListQuery,
  ListSchema,
} from '~/client/src/shared/lists/core'
import { patchQuery, setSort } from '~/client/src/shared/lists/core'

import {
  isListFieldSortEligible,
  normalizeGroupingTransition,
} from './listFieldSorting'

interface IColumnConfigHandlers {
  items: readonly IColumnConfig[]
  defaultItems: readonly IColumnConfig[]
  onChange: (
    items: Array<Pick<IColumnConfig, 'dataKey' | 'isFixed' | 'isHidden'>>,
  ) => void
  onReset: () => void
}

export function toViewOptionsProps<T>(
  query: ListQuery,
  dispatch: Dispatch<ListAction>,
  schema: ListSchema<T>,
  columns: IColumnConfigHandlers,
  groupingAllowNone: boolean,
  defaultQuery?: ListQuery,
): IViewOptionsPanelProps {
  return buildViewOptionsPanelProps({
    grouping: {
      value: query.groupBy[0] ?? null,
      ...(defaultQuery === undefined
        ? {}
        : { defaultValue: defaultQuery.groupBy[0] ?? null }),
      options: schema.fields
        .filter(field => field.groupable)
        .map(field => ({ id: field.key, label: field.label })),
      onChange: key => {
        dispatch(
          patchQuery(
            normalizeGroupingTransition(query, key ? [key] : [], schema),
          ),
        )
      },
      allowNone: groupingAllowNone,
    },
    sorting: {
      value: query.sortKey,
      ...(defaultQuery === undefined
        ? {}
        : {
            defaultValue: {
              key: defaultQuery.sortKey,
              order: defaultQuery.sortOrder,
            },
          }),
      options: schema.fields
        .filter(field => isListFieldSortEligible(field, query.groupBy[0]))
        .map(field => ({ id: field.key, caption: field.label })),
      order: query.sortOrder,
      onChange: (key, order) => {
        dispatch(setSort(key || null, order ?? SortOrder.DEFAULT))
      },
    },
    columns,
  })
}
